// 🖥 화면 소스 선택 모달 (공용) — 데스크톱(Tauri) Rust screen_sources() 의 모니터·창 목록을 썸네일 카드로 보여 준다.
//   화면공유(CallDock, 2026-08-07)에 있던 픽커를 꺼내 공용화 (2026-09-04 원격 "캡처할 때 다른 모니터의 화면도 캡쳐되게").
//   썸네일은 이 컴포넌트가 직접 받는다(screen_thumb, 도착하는 대로 채움) — 호출부는 sources 만 넘긴다.
//   🔴 바깥 클릭으로 닫지 않는다(입력·선택 있는 모달 규약) — X·ESC·취소만.
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '../icons';
import { isTauri } from '../../desktop/env';

/** Rust screen_sources() 응답 한 건 (모니터/창) */
export interface CaptureSource { kind: 'monitor' | 'window'; id: number; name: string; width: number; height: number }

/** 데스크톱 캡처 대상 목록 — Tauri 가 아니거나 실패하면 빈 배열 */
export async function listScreenSources(): Promise<CaptureSource[]> {
	if (!isTauri()) return [];
	try {
		const { invoke } = await import('@tauri-apps/api/core');
		return await invoke<CaptureSource[]>('screen_sources');
	} catch { return []; }
}

export default function ScreenSourcePicker({ sources, title, onPick, onCancel }: {
	sources: CaptureSource[];
	title: string;
	onPick: (s: CaptureSource) => void;
	onCancel: () => void;
}) {
	const { t } = useTranslation();
	const [thumbs, setThumbs] = useState<Record<string, string>>({});
	// 🖼 썸네일 비동기 로드 — 도착하는 대로 채우고, 닫힐 때 objectURL 해제
	useEffect(() => {
		const urls: string[] = [];
		let alive = true;
		void (async () => {
			const { invoke } = await import('@tauri-apps/api/core');
			sources.forEach((s) => {
				void invoke<ArrayBuffer>('screen_thumb', { kind: s.kind, id: s.id })
					.then((buf) => {
						if (!alive) return;
						const u = URL.createObjectURL(new Blob([buf], { type: 'image/jpeg' }));
						urls.push(u);
						setThumbs((cur) => ({ ...cur, [`${s.kind}#${s.id}`]: u }));
					})
					.catch(() => undefined);   // 창 닫힘 등 — 아이콘 플레이스홀더 유지
			});
		})();
		return () => { alive = false; urls.forEach((u) => { try { URL.revokeObjectURL(u); } catch { /* */ } }); };
	}, [sources]);
	useEffect(() => {
		const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onCancel(); };
		window.addEventListener('keydown', onKey);
		return () => window.removeEventListener('keydown', onKey);
	}, [onCancel]);
	const monitors = sources.filter((s) => s.kind === 'monitor');
	const windows = sources.filter((s) => s.kind === 'window');
	return (
		<div role="dialog" aria-modal="true" data-testid="share-picker"
			className="fixed inset-0 z-[1255] flex items-center justify-center bg-slate-900/60 p-4">
			<div className="flex max-h-[80vh] w-[min(460px,94vw)] flex-col overflow-hidden rounded-2xl bg-surface shadow-2xl">
				<header className="flex items-center gap-2 border-b border-line-subtle px-5 py-3">
					<span className="text-fg-muted"><Icon name="monitor" size={18} /></span>
					<div className="text-ui-base font-bold text-fg">{title}</div>
					<button type="button" onClick={onCancel}
						className="ml-auto rounded-md p-1.5 text-fg-muted hover:bg-hover" title={t('call.sharePickCancel', { defaultValue: '취소' })}>
						<Icon name="x" size={16} />
					</button>
				</header>
				<div className="flex-1 overflow-y-auto p-3">
					{/* 🖼 썸네일 카드 그리드 (2026-08-07 사용자 — 글자 목록 대신 화면 미리보기) */}
					<div className="mb-1.5 px-1 text-ui-xs font-semibold text-fg-faint">{t('call.sharePickMonitors', { defaultValue: '전체 화면' })}</div>
					<div className="grid grid-cols-2 gap-2">
						{monitors.map((s, i) => (
							<button key={`m${s.id}`} type="button" onClick={() => onPick(s)} data-testid="share-picker-monitor"
								className="overflow-hidden rounded-xl border border-line text-left transition hover:border-class-blue hover:shadow-md">
								<div className="flex aspect-video w-full items-center justify-center bg-gray-900/90">
									{thumbs[`monitor#${s.id}`]
										? <img src={thumbs[`monitor#${s.id}`]} alt="" className="h-full w-full object-contain" />
										: <span className="text-white/30"><Icon name="monitor" size={28} /></span>}
								</div>
								<div className="flex items-center gap-1 px-2 py-1.5">
									<span className="min-w-0 flex-1 truncate text-ui-xs font-medium text-fg">{t('call.sharePickMonitorN', { defaultValue: '모니터 {{n}}', n: i + 1 })}</span>
									<span className="shrink-0 text-[10px] text-fg-faint">{s.width}×{s.height}</span>
								</div>
							</button>
						))}
					</div>
					{windows.length > 0 && (
						<div className="mb-1.5 mt-3 px-1 text-ui-xs font-semibold text-fg-faint">{t('call.sharePickWindows', { defaultValue: '창' })}</div>
					)}
					<div className="grid grid-cols-2 gap-2">
						{windows.map((s) => (
							<button key={`w${s.id}`} type="button" onClick={() => onPick(s)} data-testid="share-picker-window"
								className="overflow-hidden rounded-xl border border-line text-left transition hover:border-class-blue hover:shadow-md">
								<div className="flex aspect-video w-full items-center justify-center bg-gray-900/90">
									{thumbs[`window#${s.id}`]
										? <img src={thumbs[`window#${s.id}`]} alt="" className="h-full w-full object-contain" />
										: <span className="text-white/30"><Icon name="layout-dashboard" size={28} /></span>}
								</div>
								<div className="px-2 py-1.5">
									<span className="block truncate text-ui-xs font-medium text-fg" title={s.name}>{s.name}</span>
								</div>
							</button>
						))}
					</div>
				</div>
			</div>
		</div>
	);
}
